import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UserSavedRequestService} from '../../../../../../services/apiServices/business/user-saved-request.service';
import {TranslateService} from '@ngx-translate/core';
import {SearchCriteria} from '../../../../../../model/search-criteria';
import {DateFiltered} from '../../../../../../services/apiServices/business/search.service';
import {CommonService} from '../../../../../../services/apiServices/business/common.service';

/**
 * Component that handle request saving (including updates)
 */
@Component({
  selector: 'app-add-or-update-saved-request',
  templateUrl: './add-or-update-saved-request.component.html',
  styleUrls: ['./add-or-update-saved-request.component.scss'],
})
export class AddOrUpdateSavedRequestComponent implements OnInit {

  /**
   * Output Binding for modal opening
   */
  @Output() public displayComponentChange: EventEmitter<boolean>;

  /**
   * Input Binding for modal opening
   */
  @Input('displayComponent') public displayComponent: boolean;

  /**
   * the name of the selected request
   */
  @Input('selectedRequestName') public selectedRequestName: string;

  /**
   * if the selected option inside search was Today
   */
  @Input('bDepotDay') public bDepotDay: boolean;

  /**
   * if the selected option inside search was current month
   */
  @Input('bDepotMonth') public bDepotMonth: boolean;

  /**
   * if the selected option inside search was current week
   */
  @Input('bDepotWeek') public bDepotWeek: boolean;

  /**
   * if the selected option inside search was yesterday
   */
  @Input('bDepotYesterday') public bDepotYesterday: boolean;

  /**
   * if the search is restricted to deposits
   */
  @Input('bLimitedDeposit') public bLimitedDeposit: boolean;

  /**
   * if the search is restricted to sent
   */
  @Input('bLimitedSent') public bLimitedSent: boolean;

  /**
   * number of custom week search
   */
  @Input('iDepotWeekNum') public iDepotWeekNum: number;

  /**
   * number of custom month search
   */
  @Input('iDepotMonthNum') public iDepotMonthNum: number;

  /**
   * Beginning date for custom period search
   */
  @Input('dateDepotBegin') public dateDepotBegin: Date;

  /**
   * End date for custom period search
   */
  @Input('dateDepotEnd') public dateDepotEnd: Date;

  /**
   * the dateOption currently selected (usefull to filter fields for request)
   */
  @Input('selectedDateOption') public selectedDateOption;

  /**
   * The list of search criterias
   */
  @Input('searchCriterias') public searchCriterias: SearchCriteria[];

  /**
   * If it's a new request: the request Name from input field
   */
  public newRequestName = '';

  /**
   * The default request label (only usefull to compare)
   */
  private defaultRequestLabel: string;

  /**
   * Title of message display on request success
   */
  private successRequestTitle: string;

  /**
   * Message display on request success
   */
  private successRequestMessage: string;
  /**
   * Title of message display on request error
   */
  private errorRequestTitle: string;

  /**
   * Message display on request error
   */
  private errorRequestMessage: string;

  /**
   * instantiate variables
   * @param {UserSavedRequestService} userSavedRequestService - handle save request
   * @param commonService
   * @param {TranslateService} translate - handle translation for component
   */
  constructor(private userSavedRequestService: UserSavedRequestService, private commonService: CommonService, private translate: TranslateService) {
    this.displayComponentChange = new EventEmitter<boolean>();
  }

  /**
   * Init variables
   */
  public ngOnInit(): void {
    this.getTradForComponent();
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('SAVED_REQUEST.NEW_REQUEST_LABEL').subscribe((translation: string) => {
      this.defaultRequestLabel = translation;
    });
    this.translate.get('TOASTER.SUCCESS_MESSAGE_TITLE').subscribe((translation: string) => {
      this.successRequestTitle = translation;
    });
    this.translate.get('TOASTER.SAVE_REQUEST_SUCCESS_MESSAGE').subscribe((translation: string) => {
      this.successRequestMessage = translation;
    });
    this.translate.get('TOASTER.ERROR_MESSAGE_TITLE').subscribe((translation: string) => {
      this.errorRequestTitle = translation;
    });
    this.translate.get('TOASTER.SAVE_REQUEST_ERROR_MESSAGE').subscribe((translation: string) => {
      this.errorRequestMessage = translation;
    });
  }

  /**
   * Determine the kind of request, more precisley if it's a new one or if it's an old one that we should update
   * @return {boolean}
   */
  public isABrandNewRequest(): boolean {
    return this.selectedRequestName === this.defaultRequestLabel;
  }

  /**
   * Save request using UserSavedRequestService and all bindings
   */
  public saveNewRequest(): void {
    const name = this.isABrandNewRequest() ? this.newRequestName : this.selectedRequestName;

    switch (this.selectedDateOption) {
      case DateFiltered.OTHER_WEEK:
        this.iDepotMonthNum = null;
        this.dateDepotBegin = null;
        this.dateDepotEnd = null;
        break;
      case DateFiltered.OTHER_MONTH:
        this.iDepotWeekNum = null;
        this.dateDepotBegin = null;
        this.dateDepotEnd = null;
        break;
      case DateFiltered.CUSTOM_PERIOD:
        this.iDepotWeekNum = null;
        this.iDepotMonthNum = null;
        break;
      default:
        this.iDepotWeekNum = null;
        this.iDepotMonthNum = null;
        this.dateDepotBegin = null;
        this.dateDepotEnd = null;
        break;
    }

    this.userSavedRequestService.saveNewRequest(name, this.bDepotDay, this.bDepotMonth,
      this.bDepotWeek, this.bDepotYesterday, this.bLimitedDeposit,
      this.bLimitedSent, this.iDepotWeekNum, this.iDepotMonthNum, this.dateDepotBegin, this.dateDepotEnd, this.searchCriterias).subscribe(res => {
      if (res) {

        this.newRequestName = '';
        this.displayComponentChange.emit(false);
        this.commonService.addToast({
          life: 3000,
          severity: 'success',
          summary: this.successRequestTitle,
          detail: this.successRequestMessage
        });

      } else {
        this.newRequestName = '';
        this.displayComponentChange.emit(false);
        this.commonService.addToast({
          life: 3000,
          severity: 'error',
          summary: this.errorRequestTitle,
          detail: this.errorRequestMessage
        });
      }
    });
  }

  /**
   * if the new search that whe wants to update is valid
   * @return {boolean}
   */
  public isRequestOk(): boolean {
    return this.isABrandNewRequest() ? this.newRequestName.length !== 0 && this.newRequestName !== this.selectedRequestName : true;
  }
}
